import React from 'react';
import { Box, Typography } from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';
import LaunchIcon from '@mui/icons-material/Launch';
import PhoneIcon from '@mui/icons-material/Phone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';

// Some templates come back from Meta with escaped line breaks ("\\n" as two characters);
// turn those into real line breaks so the message reads as it does in WhatsApp
export const cleanText = (text = '') => text.replace(/\\r\\n|\\n|\\r/g, '\n').replace(/\\t/g, '\t');
export const oneLine = (text = '') => cleanText(text).replace(/\s+/g, ' ').trim();

// Highlight {{1}} style variables, or substitute example values when given
export const withVariables = (text = '', examples) =>
  cleanText(text).split(/(\{\{[^}]+\}\})/g).map((part, index) => {
    const match = part.match(/^\{\{\s*(\d+)\s*\}\}$/);
    if (!match && !/^\{\{[^}]+\}\}$/.test(part)) return <React.Fragment key={index}>{part}</React.Fragment>;
    const value = match && examples ? examples[Number(match[1]) - 1] : null;
    return (
      <Box
        component="span"
        key={index}
        sx={{ bgcolor: 'rgba(14, 124, 134, 0.12)', color: '#0B5F66', borderRadius: 0.5, px: 0.4, fontWeight: 500 }}
      >
        {value || part}
      </Box>
    );
  });

const WA_TEXT = '#111B21';
const WA_MUTED = '#667781';
const WA_LINK = '#027EB5';
const WA_RULE = '#E9EDEF';

const MEDIA_ICONS = {
  IMAGE: ImageOutlinedIcon,
  VIDEO: VideocamOutlinedIcon,
  DOCUMENT: DescriptionOutlinedIcon,
  LOCATION: PlaceOutlinedIcon
};

const BUTTON_ICONS = {
  QUICK_REPLY: ReplyIcon,
  URL: LaunchIcon,
  PHONE_NUMBER: PhoneIcon,
  VOICE_CALL: WhatsAppIcon,
  COPY_CODE: ContentCopyIcon,
  OTP: ContentCopyIcon,
  CATALOG: StorefrontOutlinedIcon,
  MPM: ListAltOutlinedIcon,
  FLOW: ListAltOutlinedIcon
};

const buttonLabel = (button) => {
  if (button.type === 'COPY_CODE') return 'Copy offer code';
  if (button.type === 'OTP') {
    if (button.otp_type === 'ONE_TAP') return button.autofill_text || 'Autofill';
    if (button.otp_type === 'ZERO_TAP') return 'Code filled in automatically';
    return button.text || 'Copy code';
  }
  return button.text || '';
};

// Media or location header box; previewUrl is a local object URL set by the builder
const HeaderMedia = ({ header, height = 150 }) => {
  const Icon = MEDIA_ICONS[header.format];
  if (header.previewUrl && header.format === 'IMAGE') {
    return <Box component="img" src={header.previewUrl} alt="" sx={{ width: '100%', height, objectFit: 'cover', borderRadius: 1.5, display: 'block', mb: 1 }} />;
  }
  if (header.previewUrl && header.format === 'VIDEO') {
    return <Box component="video" src={header.previewUrl} muted sx={{ width: '100%', height, objectFit: 'cover', borderRadius: 1.5, display: 'block', mb: 1, bgcolor: '#000' }} />;
  }
  return (
    <Box sx={{ bgcolor: '#DDE3E8', borderRadius: 1.5, height: header.format === 'DOCUMENT' ? 64 : height, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1, px: 1.5 }}>
      {Icon && <Icon sx={{ fontSize: header.format === 'DOCUMENT' ? 28 : 40, color: '#8696A0' }} />}
      {header.format === 'DOCUMENT' && (
        <Typography noWrap sx={{ fontSize: '0.8rem', color: WA_MUTED }}>{header.fileName || 'Document'}</Typography>
      )}
      {header.format === 'LOCATION' && (
        <Typography sx={{ fontSize: '0.8rem', color: WA_MUTED }}>Location is added when sending</Typography>
      )}
    </Box>
  );
};

const ButtonList = ({ buttons }) => (
  buttons.length > 0 ? (
    <Box sx={{ mt: 0.5, borderTop: `1px solid ${WA_RULE}` }}>
      {buttons.slice(0, 3).map((button, index) => {
        const Icon = BUTTON_ICONS[button.type] || LaunchIcon;
        return (
          <Box
            key={index}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, py: 1, color: WA_LINK, fontSize: '0.875rem', fontWeight: 500, borderTop: index ? `1px solid ${WA_RULE}` : 'none' }}
            title={button.url || button.phone_number || ''}
          >
            <Icon sx={{ fontSize: 16 }} />
            {buttonLabel(button)}
          </Box>
        );
      })}
      {buttons.length > 3 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, py: 1, color: WA_LINK, fontSize: '0.875rem', fontWeight: 500, borderTop: `1px solid ${WA_RULE}` }}>
          <ListAltOutlinedIcon sx={{ fontSize: 16 }} />
          See all options
        </Box>
      )}
    </Box>
  ) : null
);

const Bubble = ({ children, width = 360 }) => (
  <Box sx={{ bgcolor: '#fff', borderRadius: '0 10px 10px 10px', boxShadow: '0 1px 1px rgba(11, 20, 26, 0.13)', p: 1, maxWidth: width, width: '100%' }}>
    {children}
  </Box>
);

/**
 * Renders a template (Meta component format) the way it appears in WhatsApp.
 * `examples` optionally substitutes body variables: { body: [...], header: [...] }.
 */
const TemplatePreview = ({ template, examples }) => {
  const components = template?.components || [];
  const find = (type) => components.find((c) => c.type === type);
  const header = find('HEADER');
  const body = find('BODY');
  const footer = find('FOOTER');
  const lto = find('LIMITED_TIME_OFFER');
  const carousel = find('CAROUSEL');
  const buttons = find('BUTTONS')?.buttons || [];

  const isAuth = template?.category === 'AUTHENTICATION';
  const bodyText = isAuth
    ? `{{1}} is your verification code.${body?.add_security_recommendation ? ' For your security, do not share this code.' : ''}`
    : body?.text;
  const footerText = footer?.code_expiration_minutes
    ? `This code expires in ${footer.code_expiration_minutes} minutes.`
    : footer?.text;

  return (
    <Box sx={{ bgcolor: '#EFEAE2', borderRadius: 2, p: { xs: 2, sm: 3 } }}>
      <Bubble>
        {header && header.format !== 'TEXT' && <HeaderMedia header={header} />}

        {lto && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#F0F2F5', borderRadius: 1.5, px: 1.25, py: 1, mb: 1 }}>
            <LocalOfferOutlinedIcon sx={{ fontSize: 20, color: '#128C7E' }} />
            <Box>
              <Typography sx={{ fontWeight: 700, color: WA_TEXT, fontSize: '0.9rem' }}>{lto.limited_time_offer?.text || 'Offer'}</Typography>
              {lto.limited_time_offer?.has_expiration && (
                <Typography sx={{ color: WA_MUTED, fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTimeIcon sx={{ fontSize: 12 }} /> Ends in 2 days
                </Typography>
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ px: 0.5 }}>
          {header?.format === 'TEXT' && header.text && (
            <Typography sx={{ fontWeight: 700, color: WA_TEXT, mb: 0.5, fontSize: '0.95rem' }}>
              {withVariables(header.text, examples?.header)}
            </Typography>
          )}
          {bodyText && (
            <Typography sx={{ color: WA_TEXT, fontSize: '0.9rem', lineHeight: 1.45, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {withVariables(bodyText, isAuth ? ['123456'] : examples?.body)}
            </Typography>
          )}
          {footerText && (
            <Typography sx={{ color: WA_MUTED, fontSize: '0.8rem', mt: 0.75, whiteSpace: 'pre-wrap' }}>{cleanText(footerText)}</Typography>
          )}
          <Typography sx={{ color: WA_MUTED, fontSize: '0.7rem', textAlign: 'right', mt: 0.25 }}>12:00</Typography>
        </Box>
        <ButtonList buttons={buttons} />
      </Bubble>

      {carousel && (
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', mt: 1, pb: 0.5, scrollSnapType: 'x mandatory' }}>
          {(carousel.cards || []).map((card, index) => {
            const cardHeader = (card.components || []).find((c) => c.type === 'HEADER');
            const cardBody = (card.components || []).find((c) => c.type === 'BODY');
            const cardButtons = (card.components || []).find((c) => c.type === 'BUTTONS')?.buttons || [];
            return (
              <Box key={index} sx={{ flex: '0 0 220px', scrollSnapAlign: 'start' }}>
                <Bubble width={220}>
                  {cardHeader && <HeaderMedia header={cardHeader} height={120} />}
                  {cardBody?.text && (
                    <Typography sx={{ color: WA_TEXT, fontSize: '0.85rem', px: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {withVariables(cardBody.text, examples?.cards?.[index])}
                    </Typography>
                  )}
                  <ButtonList buttons={cardButtons} />
                </Bubble>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default TemplatePreview;
